import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Carousel,
  Button,
  Statistic,
  Row,
  Col,
  Space,
  Typography,
  Tag,
  Spin,
  Alert,
  Divider,
  Table,
  Breadcrumb,
  Tabs,
  Modal,
  Input,
  Form,
  InputNumber,
  Progress,
  Tooltip
} from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  ReloadOutlined,
  BankOutlined,
  DollarOutlined,
  StockOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  HomeOutlined,
  LineChartOutlined,
  CalendarOutlined,
  SaveOutlined,
  RobotOutlined,
  EyeOutlined,
  EyeInvisibleOutlined
} from '@ant-design/icons';
import tradingService from '../services/tradingService';
import configService from '../services/configService';
import dailyPriceService from '../services/dailyPriceService';
import dataManagementService from '../services/dataManagementService';
import aiAnalysisService from '../services/aiAnalysisService';
import DailyPriceChart from '../components/DailyPriceChart';
import DailyPriceCalendar from '../components/DailyPriceCalendar';
import PortfolioAnalysisChart from '../components/PortfolioAnalysisChart';
import AccountBalanceHeader from '../components/AccountBalance/AccountBalanceHeader';
import DollarCostAveragingModal from '../components/AccountBalance/DollarCostAveragingModal';
import StockAnalysisModal from '../components/StockAnalysisModal';
import AccountDetailCard from '../components/AccountBalance/AccountDetailCard';

const { Title, Text } = Typography;

const AccountBalance = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountsData, setAccountsData] = useState({});
  const [holdingsData, setHoldingsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [accountLoadingStates, setAccountLoadingStates] = useState({}); // 계좌별 로딩 상태
  const [carouselRef, setCarouselRef] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [error, setError] = useState(null);
  const [refreshingAccounts, setRefreshingAccounts] = useState(new Set()); // 개별 계좌 리프레쉬 상태
  const [savingData, setSavingData] = useState(false); // 수동 저장 상태
  const [manualCashModalVisible, setManualCashModalVisible] = useState(false);
  const [manualCashAccount, setManualCashAccount] = useState(null);
  const [manualCashSaving, setManualCashSaving] = useState(false);
  
  // 데이터 캐싱 관련 상태
  const [lastLoadTime, setLastLoadTime] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [accountTimestamps, setAccountTimestamps] = useState({}); // 계좌별 데이터 저장 시간
  
  // 물타기 모달 관련 상태
  const [dollarCostModalVisible, setDollarCostModalVisible] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [additionalPrice, setAdditionalPrice] = useState(0);
  const [additionalQuantity, setAdditionalQuantity] = useState(0);
  const [calculationResult, setCalculationResult] = useState(null);
  const [form] = Form.useForm();
  const [manualCashForm] = Form.useForm();
  
  // AI 분석 모달 관련 상태
  const [aiModalVisible, setAiModalVisible] = useState(false);
  
  // 자산 가리기 상태
  const [hideAmounts, setHideAmounts] = useState(() => {
    const saved = localStorage.getItem('hideAmounts');
    return saved === 'true';
  });
  
  // 자산 가리기 토글 핸들러
  const toggleHideAmounts = useCallback(() => {
    setHideAmounts(prev => {
      const newValue = !prev;
      localStorage.setItem('hideAmounts', String(newValue));
      return newValue;
    });
  }, []);
  
  // 금액 표시 헬퍼 함수
  const formatAmount = useCallback((amount) => {
    if (hideAmounts) {
      return '******';
    }
    return typeof amount === 'number' ? `₩${Math.round(amount).toLocaleString()}` : amount;
  }, [hideAmounts]);
  
  // 최신 state 값을 추적하기 위한 ref
  const accountsDataRef = React.useRef(accountsData);
  const holdingsDataRef = React.useRef(holdingsData);
  
  // ref 업데이트
  React.useEffect(() => {
    accountsDataRef.current = accountsData;
  }, [accountsData]);
  
  React.useEffect(() => {
    holdingsDataRef.current = holdingsData;
  }, [holdingsData]);

  // 캐시 키 생성
  const getCacheKey = useCallback(() => {
    return 'accountBalanceData';
  }, []);

  // 캐시에서 데이터 로드
  const loadFromCache = useCallback(() => {
    try {
      const cacheKey = getCacheKey();
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        const { accountsData: cachedAccountsData, holdingsData: cachedHoldingsData, timestamp, accountTimestamps: cachedAccountTimestamps } = parsed;
        
        // 캐시된 데이터가 있으면 즉시 표시
        setAccountsData(cachedAccountsData || {});
        setHoldingsData(cachedHoldingsData || {});
        setAccountTimestamps(cachedAccountTimestamps || {});
        setLastLoadTime(new Date(timestamp));
        setIsInitialLoad(false);
        
        // console.log('✅ 캐시된 데이터 로드 완료:', new Date(timestamp));
        return true;
      }
    } catch (error) {
      console.error('❌ 캐시 데이터 로드 실패:', error);
      localStorage.removeItem(getCacheKey());
    }
    return false;
  }, [getCacheKey]);

  // 캐시에 데이터 저장
  const saveToCache = useCallback((accountsData, holdingsData, timestamps) => {
    try {
      const cacheKey = getCacheKey();
      const timestamp = new Date().toISOString();
      const cacheData = {
        accountsData,
        holdingsData,
        accountTimestamps: timestamps,
        timestamp
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      setLastLoadTime(new Date(timestamp));
      // console.log('✅ 데이터 캐시 저장 완료:', timestamp);
    } catch (error) {
      console.error('❌ 캐시 저장 실패:', error);
    }
  }, [getCacheKey]);

  // 강제 새로고침 (캐시 무시) - loadAccountsData 정의 후 다시 정의됨
  const forceRefresh = useCallback(async () => {
    // console.log('🔄 강제 새로고침 시작...');
    
    // 캐시 삭제
    localStorage.removeItem(getCacheKey());
    
    // 데이터 초기화
    setAccountsData({});
    setHoldingsData({});
    setLastLoadTime(null);
    setIsInitialLoad(true);
  }, [getCacheKey]);

  // 통합 설정 로드
  const loadConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      // console.log('🔄 통합 계좌 설정 로드 중...');
      
      await configService.loadConfig();
      const allAccounts = configService.getAllAccounts();
      
      // console.log('✅ 계좌 설정 로드 완료:', allAccounts);
      setAccounts(allAccounts);
      setError(null);
      
    } catch (error) {
      console.error('❌ 계좌 설정 로드 실패:', error);
      
      // 폴백으로 기본 계좌 사용
      const fallbackAccounts = configService.getAllAccounts();
      setAccounts(fallbackAccounts);
      
      if (fallbackAccounts.length === 0) {
        setError('계좌 설정을 로드할 수 없습니다. 관리자에게 문의하세요.');
      }
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // 개별 계좌 데이터 로드 함수
  const loadSingleAccountData = useCallback(async (account) => {
    // console.log(`🔄 개별 계좌 데이터 로드 중: ${account.accountName}`);
    
    try {
      // 1. 계좌 잔고 조회
      const accountRequest = [{
        account_id: account.accountId,
        broker: account.broker,
        account_number: account.accountNumber
      }];
      
      const balanceResponse = await configService.getMultipleAccountBalances(accountRequest);
      
      if (balanceResponse?.data?.results?.[account.accountId]) {
        const accountData = balanceResponse.data.results[account.accountId];
        
        // 에러가 없는 경우에만 성공으로 처리
        if (!accountData.error) {
          // 계좌 데이터 업데이트
          setAccountsData(prev => ({ 
            ...prev, 
            [account.accountId]: accountData 
          }));
          
          // 보유 주식 데이터 업데이트
          if (accountData.holdings) {
            setHoldingsData(prev => ({ 
              ...prev, 
              [account.accountId]: { 
                holdings: accountData.holdings,
                retirement_account_info: accountData.retirement_account_info  // 위험자산 비율 정보
              }
            }));
            // console.log(`✅ 개별 계좌 데이터 로드 성공: ${account.accountName} (${accountData.holdings.length}개 종목)`);
          } else {
            setHoldingsData(prev => ({ 
              ...prev, 
              [account.accountId]: { 
                holdings: [],
                retirement_account_info: accountData.retirement_account_info  // 위험자산 비율 정보
              }
            }));
            // console.log(`✅ 개별 계좌 데이터 로드 성공: ${account.accountName} (보유 종목 없음)`);
          }
          
          // 성공 시에만 timestamp 업데이트
          setAccountTimestamps(prev => ({
            ...prev,
            [account.accountId]: accountData.last_updated || new Date().toISOString()
          }));
          
          return { success: true };
        } else {
          console.warn(`⚠️ 개별 계좌 잔고 조회 실패: ${account.accountName} - ${accountData.error}`);
          
          // 에러 상태 업데이트 (timestamp는 업데이트하지 않음)
          setAccountsData(prev => ({ 
            ...prev, 
            [account.accountId]: { error: accountData.error }
          }));
          setHoldingsData(prev => ({ 
            ...prev, 
            [account.accountId]: { holdings: [] }
          }));
          
          return { success: false, error: accountData.error };
        }
        
      } else {
        console.warn(`⚠️ 개별 계좌 잔고 조회 실패: ${account.accountName}`);
        
        // 에러 상태 업데이트
        setAccountsData(prev => ({ 
          ...prev, 
          [account.accountId]: { error: '잔고 조회 실패' }
        }));
        setHoldingsData(prev => ({ 
          ...prev, 
          [account.accountId]: { holdings: [] }
        }));
        
        return { success: false, error: '잔고 조회 실패' };
      }
      
    } catch (accountError) {
      console.error(`❌ 개별 계좌 처리 실패 (${account.accountName}):`, accountError);
      
      const errorMessage = accountError.message || '계좌 조회 실패';
      
      // 에러 상태 업데이트
      setAccountsData(prev => ({ 
        ...prev, 
        [account.accountId]: { error: errorMessage }
      }));
      setHoldingsData(prev => ({ 
        ...prev, 
        [account.accountId]: { holdings: [] }
      }));
      
      return { success: false, error: errorMessage };
    }
  }, []);

  // 계좌 데이터 로드 (개별 계좌별로 병렬 처리하여 즉시 표시)
  const loadAccountsData = useCallback(async (forceRefresh = false, isMountedRef = { current: true }) => {
    if (!accounts || accounts.length === 0) {
      return;
    }

    // 강제 새로고침이 아니고 초기 로드가 아닌 경우, 캐시된 데이터가 있으면 사용
    if (!forceRefresh && !isInitialLoad) {
      const hasCachedData = loadFromCache();
      if (hasCachedData) {
        // console.log('📦 캐시된 데이터 사용 중...');
        return;
      }
    }

    if (!isMountedRef.current) return;

    setLoading(false); // 전체 로딩은 false로 설정
    setError(null);

    // 모든 계좌를 로딩 상태로 초기화
    const initialLoadingStates = {};
    accounts.forEach(account => {
      initialLoadingStates[account.accountId] = true;
    });
    setAccountLoadingStates(initialLoadingStates);

    // console.log('🔄 계좌 잔고 데이터 로드 중...', accounts);

    const startTime = new Date();
    let newAccountsData = {};
    let newHoldingsData = {};

    // 각 계좌를 개별적으로 병렬 처리
    const loadPromises = accounts.map(async (account, index) => {
      try {
        // console.log(`📊 계좌 ${index + 1}/${accounts.length} 처리 중: ${account.accountName}`);
        
        if (!isMountedRef.current) return;
        
        // 계좌별 로딩 상태 시작
        setAccountLoadingStates(prev => ({ ...prev, [account.accountId]: true }));
        
        // 계좌 간 간격 (너무 빠른 연속 요청 방지)
        await new Promise(resolve => setTimeout(resolve, index * 300)); // 0.3초씩 지연
        
        if (!isMountedRef.current) return;
        
        const accountRequest = [{
          account_id: account.accountId,
          broker: account.broker,
          account_number: account.accountNumber
        }];
        
        const balanceResponse = await configService.getMultipleAccountBalances(accountRequest);
        
        if (!isMountedRef.current) return;
        
        if (balanceResponse?.data?.results?.[account.accountId]) {
          const accountData = balanceResponse.data.results[account.accountId];
          
          // 에러가 없는 경우에만 성공으로 처리
          if (!accountData.error) {
            // 계좌 데이터 즉시 업데이트
            setAccountsData(prev => ({ 
              ...prev, 
              [account.accountId]: accountData 
            }));
            
            // 보유 주식 데이터 즉시 업데이트
            if (accountData.holdings) {
              setHoldingsData(prev => ({ 
                ...prev, 
                [account.accountId]: { 
                  holdings: accountData.holdings,
                  retirement_account_info: accountData.retirement_account_info  // 위험자산 비율 정보
                }
              }));
              // console.log(`✅ 계좌 데이터 로드 성공: ${account.accountName} (${accountData.holdings.length}개 종목)`);
            } else {
              setHoldingsData(prev => ({ 
                ...prev, 
                [account.accountId]: { 
                  holdings: [],
                  retirement_account_info: accountData.retirement_account_info  // 위험자산 비율 정보
                }
              }));
              // console.log(`✅ 계좌 데이터 로드 성공: ${account.accountName} (보유 종목 없음)`);
            }
            
            // 성공 시에만 timestamp 업데이트
            setAccountTimestamps(prev => ({
              ...prev,
              [account.accountId]: accountData.last_updated || new Date().toISOString()
            }));
          } else {
            // 에러 상태 즉시 업데이트 (timestamp는 업데이트하지 않음)
            setAccountsData(prev => ({ 
              ...prev, 
              [account.accountId]: { error: accountData.error }
            }));
            setHoldingsData(prev => ({ 
              ...prev, 
              [account.accountId]: { holdings: [] }
            }));
          }
          
        } else {
          console.warn(`⚠️ 계좌 잔고 조회 실패: ${account.accountName}`);
          
          // 에러 상태 즉시 업데이트
          setAccountsData(prev => ({ 
            ...prev, 
            [account.accountId]: { error: '잔고 조회 실패' }
          }));
          setHoldingsData(prev => ({ 
            ...prev, 
            [account.accountId]: { holdings: [] }
          }));
        }
        
      } catch (accountError) {
        console.error(`❌ 계좌 처리 실패 (${account.accountName}):`, accountError);
        
        if (!isMountedRef.current) return;
        
        const errorMessage = accountError.message || '계좌 조회 실패';
        
        // 에러 상태 즉시 업데이트
        setAccountsData(prev => ({ 
          ...prev, 
          [account.accountId]: { error: errorMessage }
        }));
        setHoldingsData(prev => ({ 
          ...prev, 
          [account.accountId]: { holdings: [] }
        }));
        
      } finally {
        if (!isMountedRef.current) return;
        // 해당 계좌의 로딩 상태 완료
        setAccountLoadingStates(prev => ({ ...prev, [account.accountId]: false }));
      }
    });

    try {
      // 모든 계좌 로딩 완료 대기
      await Promise.all(loadPromises);
      // console.log('✅ 모든 계좌 데이터 로드 완료');
      
      if (!isMountedRef.current) return;
      
      // 로드 완료 후 캐시에 저장 - ref를 사용하여 최신 state 값 참조 (리렌더 없이)
      const timeoutId = setTimeout(() => {
        if (!isMountedRef.current) return;
        const currentAccountsData = accountsDataRef.current;
        const currentHoldingsData = holdingsDataRef.current;
        
        // 계좌별 timestamp 수집
        const timestamps = {};
        accounts.forEach(account => {
          const accountData = currentAccountsData[account.accountId];
          if (accountData && !accountData.error && accountData.last_updated) {
            timestamps[account.accountId] = accountData.last_updated;
          }
        });
        
        saveToCache(currentAccountsData, currentHoldingsData, timestamps);
        // console.log('💾 캐시 저장 완료:', { 
        //   accounts: Object.keys(currentAccountsData).length, 
        //   holdings: Object.keys(currentHoldingsData).length 
        // });
      }, 100);
      
      // timeout cleanup을 위해 저장
      return () => clearTimeout(timeoutId);
      
      setIsInitialLoad(false);
      
      const endTime = new Date();
      const loadDuration = (endTime - startTime) / 1000;
      // console.log(`⏱️ 데이터 로드 완료 (${loadDuration.toFixed(1)}초)`);
      
    } catch (error) {
      console.error('❌ 계좌 데이터 로드 실패:', error);
      
      if (!isMountedRef.current) return;
      
      // 더 구체적인 에러 메시지 설정
      let errorMessage = '일부 계좌 데이터를 불러올 수 없습니다';
      if (error.message.includes('Authentication') || error.message.includes('401')) {
        errorMessage = '로그인이 필요합니다. 다시 로그인해주세요.';
        localStorage.removeItem('token');
      } else if (error.message.includes('Network')) {
        errorMessage = '네트워크 오류가 발생했습니다. 연결을 확인해주세요.';
      } else if (error.response?.status === 403) {
        errorMessage = '접근 권한이 없습니다. 권한을 확인해주세요.';
      } else if (error.response?.status >= 500) {
        errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      setError(errorMessage);
    }
  }, [accounts, loadFromCache, saveToCache, isInitialLoad]);

  // forceRefresh 함수를 loadAccountsData 정의 후 다시 정의
  const forceRefreshWithData = useCallback(async () => {
    // console.log('🔄 강제 새로고침 시작...');
    
    // 캐시 삭제
    localStorage.removeItem(getCacheKey());
    dailyPriceService.clearCache();
    
    // 데이터 초기화
    setAccountsData({});
    setHoldingsData({});
    setLastLoadTime(null);
    setIsInitialLoad(true);
    
    // 데이터 다시 로드
    await loadAccountsData(true);
  }, [getCacheKey, loadAccountsData]);

  // 수동 저장 핸들러
  const handleManualSave = useCallback(async () => {
    setSavingData(true);
    
    try {
      const result = await dataManagementService.refreshToday();
      
      if (result.success) {
        Modal.success({
          title: '데이터 저장 완료',
          content: (
            <div>
              <p>일일 보유 현황 데이터가 성공적으로 저장되었습니다.</p>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                {new Date().toLocaleString()}
              </p>
            </div>
          ),
          onOk: () => {
            // 저장 후 데이터 새로고침
            forceRefreshWithData();
          }
        });
      } else {
        Modal.error({
          title: '데이터 저장 실패',
          content: result.error || '데이터 저장 중 오류가 발생했습니다.'
        });
      }
    } catch (error) {
      console.error('수동 저장 오류:', error);
      Modal.error({
        title: '데이터 저장 실패',
        content: '예상치 못한 오류가 발생했습니다.'
      });
    } finally {
      setSavingData(false);
    }
  }, [forceRefreshWithData]);

  // 컴포넌트 마운트 시 설정 로드
  useEffect(() => {
    let isMounted = true;
    
    const loadConfigSafely = async () => {
      if (isMounted) {
        await loadConfig();
      }
    };
    
    loadConfigSafely();
    
    return () => {
      isMounted = false;
    };
  }, [loadConfig]);

  // 초기 로드 시 캐시 확인
  useEffect(() => {
    if (isInitialLoad) {
      const hasCachedData = loadFromCache();
      if (hasCachedData) {
        // console.log('📦 초기 로드 시 캐시된 데이터 발견');
        setLoading(false);
      }
    }
  }, [isInitialLoad, loadFromCache]);

  // 계좌 설정이 로드된 후 데이터 로드
  useEffect(() => {
    let isMounted = true;
    const timeouts = [];
    
    const loadDataSafely = async () => {
      if (!configLoading && accounts.length > 0 && isMounted) {
        // 캐시된 데이터가 있으면 강제 새로고침하지 않음
        await loadAccountsData(false);
      }
    };
    
    loadDataSafely();
    
    return () => {
      isMounted = false;
      // 모든 pending timeout 취소
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading, accounts]);

  // 개별 계좌 리프레쉬 핸들러
  const handleRefreshAccount = useCallback(async (account) => {
    const accountId = account.accountId;
    
    // 이미 리프레쉬 중인 계좌는 중복 실행 방지
    if (refreshingAccounts.has(accountId)) {
      return;
    }
    
    // 리프레쉬 상태 시작
    setRefreshingAccounts(prev => new Set([...prev, accountId]));
    
    try {
      // console.log(`🔄 개별 계좌 리프레쉬 시작: ${account.accountName}`);
      
      const result = await loadSingleAccountData(account);
      
      if (result.success) {
        // console.log(`✅ 개별 계좌 리프레쉬 완료: ${account.accountName}`);
        
        // 개별 계좌 업데이트 후 캐시도 업데이트 - ref를 사용하여 최신 state 값 참조 (리렌더 없이)
        setTimeout(() => {
          const currentAccountsData = accountsDataRef.current;
          const currentHoldingsData = holdingsDataRef.current;
          
          // 최신 timestamp 수집
          const timestamps = {};
          accounts.forEach(acc => {
            const accData = currentAccountsData[acc.accountId];
            if (accData && !accData.error && accData.last_updated) {
              timestamps[acc.accountId] = accData.last_updated;
            }
          });
          
          saveToCache(currentAccountsData, currentHoldingsData, timestamps);
          // console.log(`💾 개별 계좌 캐시 업데이트 완료: ${account.accountName}`);
        }, 100);
      } else {
        console.warn(`⚠️ 개별 계좌 리프레쉬 실패: ${account.accountName} - ${result.error}`);
      }
      
    } catch (error) {
      console.error(`❌ 개별 계좌 리프레쉬 오류: ${account.accountName}`, error);
    } finally {
      // 리프레쉬 상태 종료
      setRefreshingAccounts(prev => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  }, [refreshingAccounts, loadSingleAccountData]);

  // KIS CMA 금액 또는 NH 수동 가용자금 입력 모달 열기
  const handleEditManualCash = useCallback((account) => {
    const accountData = accountsDataRef.current[account.accountId] || {};
    const isKisAccount = account.broker === 'kis';
    setManualCashAccount(account);
    manualCashForm.setFieldsValue({
      manual_amount: isKisAccount
        ? (accountData.manual_cma_balance || 0)
        : (accountData.available_balance || 0)
    });
    setManualCashModalVisible(true);
  }, [manualCashForm]);

  // KIS CMA 금액 또는 NH 수동 가용자금 저장
  const handleSaveManualCash = useCallback(async () => {
    if (!manualCashAccount) return;

    try {
      const values = await manualCashForm.validateFields();
      setManualCashSaving(true);

      await configService.updateManualCashBalance(
        manualCashAccount.accountId,
        values.manual_amount || 0
      );

      setManualCashModalVisible(false);
      await handleRefreshAccount(manualCashAccount);
    } catch (error) {
      if (error && error.errorFields) {
        return;
      }
      console.error('수동 금액 저장 실패:', error);
      Modal.error({
        title: '저장 실패',
        content: error.response?.data?.error || error.message || '수동 입력 금액을 저장하지 못했습니다.'
      });
    } finally {
      setManualCashSaving(false);
    }
  }, [manualCashAccount, manualCashForm, handleRefreshAccount]);

  // Carousel 네비게이션
  const goToPrev = () => {
    carouselRef?.prev();
  };

  const goToNext = () => {
    carouselRef?.next();
  };

  const goToSlide = (slideIndex) => {
    carouselRef?.goTo(slideIndex);
    setCurrentSlide(slideIndex);
  };

  // 계좌 데이터 가져오기 (account_id 기반)
  const getAccountData = (account) => {
    return accountsData[account.accountId] || {};
  };

  // 보유 주식 데이터 가져오기 (account_id 기반)
  const getHoldingsData = (account) => {
    const holdingsResponse = holdingsData[account.accountId];
    
    // holdingsResponse가 있고 holdings 배열이 있으면 반환, 없으면 빈 배열
    if (holdingsResponse && holdingsResponse.holdings && Array.isArray(holdingsResponse.holdings)) {
      return holdingsResponse.holdings;
    }
    
    return [];
  };

  // 보유 종목 심볼 추출
  const getHoldingSymbols = (account) => {
    const holdings = getHoldingsData(account);
    return holdings.map(holding => holding.symbol).filter(symbol => symbol);
  };

  // 계좌별 금일 손익 계산
  const getAccountTodayPnL = (account) => {
    const holdings = getHoldingsData(account);
    let todayPnL = 0;

    holdings.forEach(holding => {
      if (holding.prev_day_change !== null && holding.prev_day_change !== undefined && holding.quantity) {
        todayPnL += holding.prev_day_change * holding.quantity;
      }
    });

    return todayPnL;
  };

  // 전체 보유 종목 심볼 (중복 제거)
  const getAllHoldingSymbols = () => {
    const allSymbols = new Set();
    accounts.forEach(account => {
      const symbols = getHoldingSymbols(account);
      symbols.forEach(symbol => allSymbols.add(symbol));
    });
    return Array.from(allSymbols);
  };

  // 전체 보유 종목 정보 (심볼과 종목명 포함)
  const getAllHoldingStocks = () => {
    const stockMap = new Map();
    accounts.forEach(account => {
      const holdings = getHoldingsData(account);
      holdings.forEach(holding => {
        if (holding.symbol && !stockMap.has(holding.symbol)) {
          stockMap.set(holding.symbol, {
            symbol: holding.symbol,
            name: holding.stock_name || holding.symbol
          });
        }
      });
    });
    return Array.from(stockMap.values());
  };

  // 전체 보유 주식 데이터 (종목별로 통합)
  const getAllHoldingsData = () => {
    const holdingsMap = new Map();
    
    accounts.forEach(account => {
      const holdings = getHoldingsData(account);
      holdings.forEach(holding => {
        if (holding.symbol) {
          if (holdingsMap.has(holding.symbol)) {
            // 기존 데이터와 합산
            const existing = holdingsMap.get(holding.symbol);
            const totalQuantity = existing.quantity + holding.quantity;
            const totalEvaluationAmount = existing.evaluation_amount + holding.evaluation_amount;
            const totalProfitLoss = existing.profit_loss + holding.profit_loss;
            
            // 평균 구매 단가 계산 (가중평균)
            const totalInvestment = (existing.avg_buy_price * existing.quantity) + (holding.avg_buy_price * holding.quantity);
            const avgBuyPrice = totalQuantity > 0 ? totalInvestment / totalQuantity : 0;
            
            holdingsMap.set(holding.symbol, {
              ...existing,
              quantity: totalQuantity,
              avg_buy_price: avgBuyPrice,
              current_price: holding.current_price, // 최신 현재가 사용
              evaluation_amount: totalEvaluationAmount,
              profit_loss: totalProfitLoss,
              profit_loss_percent: totalEvaluationAmount > 0 ? (totalProfitLoss / (totalEvaluationAmount - totalProfitLoss)) * 100 : 0
            });
          } else {
            // 새로운 종목 추가
            holdingsMap.set(holding.symbol, { ...holding });
          }
        }
      });
    });
    
    return Array.from(holdingsMap.values());
  };

  // 전체 계좌 요약 통계 계산
  const getTotalStats = () => {
    let totalBalance = 0;
    let totalAvailable = 0;
    let totalPnL = 0;
    let todayPnL = 0; // 오늘 하루 손익
    const uniqueSymbols = new Set(); // 중복 제거를 위한 Set

    accounts.forEach(account => {
      const accountData = getAccountData(account);
      const holdings = getHoldingsData(account);
      
      totalBalance += accountData.total_balance || 0;
      totalAvailable += accountData.available_balance || 0;
      totalPnL += accountData.total_profit_loss || 0;

      // 중복 제거하면서 종목 개수 카운트
      holdings.forEach(holding => {
        if (holding.symbol) {
          uniqueSymbols.add(holding.symbol);
        }
        
        // 오늘 하루 손익 계산 (전일대비 변화량 × 보유 수량)
        if (holding.prev_day_change !== null && holding.prev_day_change !== undefined && holding.quantity) {
          todayPnL += holding.prev_day_change * holding.quantity;
        }
      });
    });

    return {
      totalBalance,
      totalAvailable,
      totalPnL,
      totalStocks: uniqueSymbols.size, // 중복 제거된 종목 개수
      todayPnL
    };
  };

  // AI 분석 핸들러 - 모달만 열기
  const handleAIAnalysis = (stock) => {
    // StockAnalysisModal에서 필요한 형식으로 변환
    const stockData = {
      code: stock.symbol,
      name: stock.stock_name,
      current_price: stock.current_price,
      prev_day_change: stock.prev_day_change,
      prev_day_change_percent: stock.prev_day_change_percent
    };
    setSelectedStock(stockData);
    setAiModalVisible(true);
  };

  // AI 모달 닫기 핸들러
  const handleAIModalClose = () => {
    setAiModalVisible(false);
    setSelectedStock(null);
  };

  // 물타기 모달 핸들러
  const handleDollarCostAveraging = (stock) => {
    setSelectedStock(stock);
    setAdditionalPrice(stock.current_price || 0);
    setAdditionalQuantity(0);
    setCalculationResult(null);
    form.resetFields();
    form.setFieldsValue({
      additionalPrice: stock.current_price || 0,
      additionalQuantity: 0
    });
    setDollarCostModalVisible(true);
  };

  // 물타기 계산 함수
  const calculateDollarCostAveraging = () => {
    if (!selectedStock || !additionalPrice || !additionalQuantity) {
      return;
    }

    const currentHolding = {
      quantity: selectedStock.quantity,
      avgPrice: selectedStock.avg_buy_price,
      totalAmount: selectedStock.avg_buy_price * selectedStock.quantity,
      currentPrice: selectedStock.current_price,
      evaluationAmount: selectedStock.evaluation_amount,
      profitLoss: selectedStock.profit_loss,
      profitLossPercent: selectedStock.profit_loss_percent
    };

    const additionalPurchase = {
      quantity: additionalQuantity,
      price: additionalPrice,
      amount: additionalPrice * additionalQuantity
    };

    const afterPurchase = {
      quantity: currentHolding.quantity + additionalPurchase.quantity,
      totalInvestment: currentHolding.totalAmount + additionalPurchase.amount,
      avgPrice: 0,
      evaluationAmount: 0,
      profitLoss: 0,
      profitLossPercent: 0
    };

    afterPurchase.avgPrice = afterPurchase.totalInvestment / afterPurchase.quantity;
    afterPurchase.evaluationAmount = afterPurchase.quantity * selectedStock.current_price;
    afterPurchase.profitLoss = afterPurchase.evaluationAmount - afterPurchase.totalInvestment;
    afterPurchase.profitLossPercent = (afterPurchase.profitLoss / afterPurchase.totalInvestment) * 100;

    setCalculationResult({
      before: currentHolding,
      additional: additionalPurchase,
      after: afterPurchase
    });
  };

  // 추가 매수 입력 변경 핸들러
  const handleAdditionalInputChange = (field, value) => {
    if (field === 'price') {
      setAdditionalPrice(value || 0);
    } else if (field === 'quantity') {
      setAdditionalQuantity(value || 0);
    }
  };

  // 모달 닫기 핸들러
  const handleModalClose = () => {
    setDollarCostModalVisible(false);
    setSelectedStock(null);
    setAdditionalPrice(0);
    setAdditionalQuantity(0);
    setCalculationResult(null);
    form.resetFields();
  };


  const totalStats = getTotalStats();

  if (error) {
    const isAuthError = error.includes('Authentication') || error.includes('authentication') || error.includes('credentials') || error.includes('401');
    
    return (
      <div style={{ padding: '24px' }}>
        <Breadcrumb style={{ marginBottom: '24px' }}>
          <Breadcrumb.Item href="/dashboard">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>계좌 잔고</Breadcrumb.Item>
        </Breadcrumb>

        <Card>
          <Alert
            message={isAuthError ? "로그인이 필요합니다" : "계좌 정보 로드 실패"}
            description={
              <div>
                <div>{isAuthError ? "계좌 정보를 확인하려면 로그인해주세요." : error}</div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  디버그 정보: 토큰 상태 = {localStorage.getItem('token') ? '존재' : '없음'}
                </div>
              </div>
            }
            type={isAuthError ? "warning" : "error"}
            showIcon
            action={
              <Space>
                {isAuthError ? (
                  <Button size="small" onClick={() => window.location.href = '/sign-in'}>
                    로그인
                  </Button>
                ) : (
                  <Button size="small" onClick={loadAccountsData}>
                    다시 시도
                  </Button>
                )}
                <Button size="small" onClick={() => {
                  // console.log('🔍 디버그 정보:');
                  // console.log('토큰:', localStorage.getItem('token'));
                  // console.log('에러:', error);
                  // console.log('계좌:', accounts);
                }}>
                  디버그
                </Button>
              </Space>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* 페이지 헤더 */}
      <AccountBalanceHeader
        lastLoadTime={lastLoadTime}
        hideAmounts={hideAmounts}
        onToggleHideAmounts={toggleHideAmounts}
        onManualSave={handleManualSave}
        onRefresh={forceRefreshWithData}
        loading={loading}
        savingData={savingData}
      />

      {/* 전체 계좌 요약 통계 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            {hideAmounts ? (
              <div>
                <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>총 자산</div>
                <div style={{ display: 'flex', alignItems: 'center', color: '#1890ff' }}>
                  <DollarOutlined style={{ marginRight: '8px', fontSize: '20px' }} />
                  <span style={{ fontSize: '24px', fontWeight: 'bold' }}>******</span>
                </div>
              </div>
            ) : (
              <Statistic
                title="총 자산"
                value={totalStats.totalBalance}
                precision={0}
                prefix={<DollarOutlined />}
                suffix="₩"
                valueStyle={{ color: '#1890ff' }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            {hideAmounts ? (
              <div>
                <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>가용 자금</div>
                <div style={{ display: 'flex', alignItems: 'center', color: '#52c41a' }}>
                  <DollarOutlined style={{ marginRight: '8px', fontSize: '20px' }} />
                  <span style={{ fontSize: '24px', fontWeight: 'bold' }}>******</span>
                </div>
              </div>
            ) : (
              <Statistic
                title="가용 자금"
                value={totalStats.totalAvailable}
                precision={0}
                prefix={<DollarOutlined />}
                suffix="₩"
                valueStyle={{ color: '#52c41a' }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            {hideAmounts ? (
              <div>
                <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>총 손익</div>
                <div style={{ display: 'flex', alignItems: 'center', color: totalStats.totalPnL >= 0 ? '#3f8600' : '#cf1322' }}>
                  {totalStats.totalPnL >= 0 ? <ArrowUpOutlined style={{ marginRight: '8px', fontSize: '20px' }} /> : <ArrowDownOutlined style={{ marginRight: '8px', fontSize: '20px' }} />}
                  <span style={{ fontSize: '24px', fontWeight: 'bold' }}>******</span>
                </div>
              </div>
            ) : (
              <Statistic
                title="총 손익"
                value={totalStats.totalPnL}
                precision={0}
                valueStyle={{
                  color: totalStats.totalPnL >= 0 ? '#3f8600' : '#cf1322'
                }}
                prefix={
                  totalStats.totalPnL >= 0 ? 
                  <ArrowUpOutlined /> : <ArrowDownOutlined />
                }
                suffix="₩"
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            {hideAmounts ? (
              <div>
                <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '14px', marginBottom: '8px' }}>오늘 손익</div>
                <div style={{ display: 'flex', alignItems: 'center', color: totalStats.todayPnL >= 0 ? '#3f8600' : '#cf1322' }}>
                  {totalStats.todayPnL >= 0 ? <ArrowUpOutlined style={{ marginRight: '8px', fontSize: '20px' }} /> : <ArrowDownOutlined style={{ marginRight: '8px', fontSize: '20px' }} />}
                  <span style={{ fontSize: '24px', fontWeight: 'bold' }}>******</span>
                </div>
              </div>
            ) : (
              <Statistic
                title="오늘 손익"
                value={totalStats.todayPnL}
                precision={0}
                valueStyle={{
                  color: totalStats.todayPnL >= 0 ? '#3f8600' : '#cf1322'
                }}
                prefix={
                  totalStats.todayPnL >= 0 ? 
                  <ArrowUpOutlined /> : <ArrowDownOutlined />
                }
                suffix="₩"
              />
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="보유 종목"
              value={totalStats.totalStocks}
              prefix={<StockOutlined />}
              suffix="개"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 포트폴리오 분석 섹션 */}
      {accounts.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <Title level={3} style={{ marginBottom: '24px' }}>
            <LineChartOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            포트폴리오 분석
          </Title>
          
          <Tabs
            defaultActiveKey="portfolio"
            items={[
              {
                key: 'portfolio',
                label: (
                  <span>
                    <LineChartOutlined />
                    자산 변화
                  </span>
                ),
                children: (
                  <PortfolioAnalysisChart
                    accounts={accounts}
                    accountsData={accountsData}
                    holdingsData={holdingsData}
                    title="포트폴리오 자산 변화"
                    hideAmounts={hideAmounts}
                  />
                )
              },
              {
                key: 'stocks',
                label: (
                  <span>
                    <StockOutlined />
                    종목별 분석
                  </span>
                ),
                children: (
                  <DailyPriceChart
                    stockSymbols={getAllHoldingSymbols()}
                    stockList={getAllHoldingStocks()}
                    holdingsData={getAllHoldingsData()}
                    title="보유 종목 종가 추이"
                    accounts={accounts}
                    accountsData={accountsData}
                    rawHoldingsData={holdingsData}
                    hideAmounts={hideAmounts}
                  />
                )
              },
              {
                key: 'calendar',
                label: (
                  <span>
                    <CalendarOutlined />
                    종가 캘린더
                  </span>
                ),
                children: (
                  <DailyPriceCalendar
                    stockSymbols={getAllHoldingSymbols()}
                    stockList={getAllHoldingStocks()}
                    title="보유 종목 종가 캘린더"
                  />
                )
              }
            ]}
          />
        </div>
      )}

      {/* 계좌 선택 및 네비게이션 */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <BankOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
              <Title level={5} style={{ margin: 0 }}>
                계좌별 상세 정보
              </Title>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadAccountsData}
                loading={loading}
                size="small"
              >
                새로고침
              </Button>
              <Button
                icon={<LeftOutlined />}
                onClick={goToPrev}
                disabled={loading || accounts.length === 0}
                size="small"
              />
              <Text>
                {accounts.length > 0 ? `${currentSlide + 1} / ${accounts.length}` : '0 / 0'}
              </Text>
              <Button
                icon={<RightOutlined />}
                onClick={goToNext}
                disabled={loading || accounts.length === 0}
                size="small"
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 계좌 요약 카드 */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]}>
          {accounts.map((account, index) => {
            const accountData = getAccountData(account);
            const isActive = currentSlide === index;
            const isLoading = accountLoadingStates[account.accountId] || false;
            
            return (
              <Col key={`${account.broker}-${account.accountNumber}`} xs={24} sm={12} md={8} lg={6}>
                <Card
                  size="small"
                  hoverable
                  onClick={() => goToSlide(index)}
                  style={{
                    cursor: 'pointer',
                    border: isActive ? `2px solid ${account.color}` : '1px solid #d9d9d9',
                    backgroundColor: isActive ? '#f6ffed' : 'white'
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong>{account.accountName}</Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Tag color={account.color}>{account.brokerName}</Tag>
                        {isLoading && (
                          <Spin size="small" />
                        )}
                      </div>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {account.accountNumber}
                    </Text>
                    {isLoading ? (
                      <div style={{ textAlign: 'center', padding: '10px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>로딩 중...</Text>
                      </div>
                    ) : (
                      <>
                        {accountData.total_balance !== undefined && (
                          hideAmounts ? (
                            <div>
                              <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: '12px', marginBottom: '4px' }}>총 자산</div>
                              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>******</div>
                            </div>
                          ) : (
                            <Statistic
                              title="총 자산"
                              value={accountData.total_balance}
                              precision={0}
                              suffix="₩"
                              valueStyle={{ fontSize: '14px' }}
                            />
                          )
                        )}
                        {accountData.total_profit_loss !== undefined && (
                          <div style={{ 
                            color: accountData.total_profit_loss >= 0 ? '#52c41a' : '#ff4d4f',
                            fontSize: '12px'
                          }}>
                            {hideAmounts ? '******' : (
                              <>
                                {accountData.total_profit_loss >= 0 ? '+' : ''}
                                ₩{accountData.total_profit_loss.toLocaleString()}
                              </>
                            )}
                          </div>
                        )}
                        {accountTimestamps[account.accountId] && (
                          <div style={{ marginTop: '8px', fontSize: '10px', color: '#999' }}>
                            업데이트: {new Date(accountTimestamps[account.accountId]).toLocaleTimeString()}
                          </div>
                        )}
                      </>
                    )}
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* 계좌 상세 정보 Carousel */}
      {loading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>
              <Text>계좌 정보를 불러오는 중...</Text>
            </div>
          </div>
        </Card>
      ) : accounts.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <BankOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Title level={4} type="secondary">등록된 계좌가 없습니다</Title>
            <Text type="secondary">계좌를 등록하여 잔고 정보를 확인하세요.</Text>
          </div>
        </Card>
      ) : (
        <Carousel
          ref={setCarouselRef}
          afterChange={setCurrentSlide}
          dots={false}
          infinite={false}
        >
          {accounts.map((account) => {
            const accountData = getAccountData(account);
            const holdings = getHoldingsData(account);
            const isLoading = accountLoadingStates[account.accountId] || false;
            const isRefreshing = refreshingAccounts.has(account.accountId);
            const accountTimestamp = accountTimestamps[account.accountId];

            return (
              <div key={`${account.broker}-${account.accountNumber}`}>
                <AccountDetailCard
                  account={account}
                  accountData={accountData}
                  holdings={holdings}
                  holdingsData={holdingsData[account.accountId]}
                  isLoading={isLoading}
                  isRefreshing={isRefreshing}
                  accountTimestamp={accountTimestamp}
                  hideAmounts={hideAmounts}
                  onRefresh={handleRefreshAccount}
                  onEditManualCash={handleEditManualCash}
                  onDollarCostAveraging={handleDollarCostAveraging}
                  onAIAnalysis={handleAIAnalysis}
                />
              </div>
            );
          })}
        </Carousel>
      )}

      {/* AI 주가 분석 모달 */}
      <StockAnalysisModal
        visible={aiModalVisible}
        selectedStock={selectedStock}
        onClose={handleAIModalClose}
        analysisType="market"  // Account Balance는 시황 분석
      />

      {/* 물타기 모달 */}
      <DollarCostAveragingModal
        visible={dollarCostModalVisible}
        selectedStock={selectedStock}
        onClose={handleModalClose}
      />

      {/* KIS CMA 금액 / NH 수동 가용자금 입력 모달 */}
      <Modal
        title={manualCashAccount?.broker === 'kis' ? 'CMA 금액 입력' : 'NH 수동 가용자금 입력'}
        visible={manualCashModalVisible}
        onOk={handleSaveManualCash}
        onCancel={() => setManualCashModalVisible(false)}
        confirmLoading={manualCashSaving}
        okText="저장"
        cancelText="취소"
      >
        <Form form={manualCashForm} layout="vertical">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
            message={manualCashAccount?.accountName}
            description={manualCashAccount?.broker === 'kis'
              ? '입력한 CMA 금액은 이 KIS 계좌의 기존 가용 자금과 총 자산에 합산되어 표시됩니다.'
              : 'NH DC/연금 계좌는 실제 API 잔고 조회가 불가능해 입력한 가용자금을 총 자산과 포트폴리오 계산에 반영합니다.'}
          />
          <Form.Item
            label={manualCashAccount?.broker === 'kis' ? 'CMA 금액' : '가용 자금'}
            name="manual_amount"
            rules={[
              { required: true, message: manualCashAccount?.broker === 'kis' ? 'CMA 금액을 입력하세요.' : '가용 자금을 입력하세요.' },
              { type: 'number', min: 0, message: '0원 이상 입력하세요.' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={0}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/₩\s?|(,*)/g, '')}
              addonAfter="₩"
            />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default AccountBalance;